
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  FileText,
  DollarSign,
  Package,
} from "lucide-react";

const ManageQuestionBanks = () => {
  const [questionBanks, setQuestionBanks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    price: 0,
    questionCount: 0,
    status: "published",
  });

  useEffect(() => {
    fetchQuestionBanks();
  }, []);

  const fetchQuestionBanks = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get("/api/question-banks");
      setQuestionBanks(response.data);
    } catch (error) {
      console.error("Error fetching question banks:", error);
      toast.error("Failed to load question banks");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUpdate = async () => {
    try {
      setIsLoading(true);

      if (selectedBank) {
        // Update
        await axios.put(`/api/question-banks/${selectedBank._id}`, formData);
        toast.success("Question bank updated successfully");
      } else {
        // Create
        await axios.post("/api/question-banks", formData);
        toast.success("Question bank created successfully");
      }

      setIsModalOpen(false);
      setSelectedBank(null);
      resetFormData();
      fetchQuestionBanks();
    } catch (error) {
      console.error("Error saving question bank:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to save question bank. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/question-banks/${id}`);
      toast.success("Question bank deleted successfully");
      fetchQuestionBanks();
    } catch (error) {
      console.error("Error deleting question bank:", error);
      toast.error("Failed to delete question bank");
    }
  };

  const handleEdit = (bank) => {
    setSelectedBank(bank);
    setFormData({
      name: bank.name,
      description: bank.description,
      category: bank.category,
      price: bank.price,
      questionCount: bank.questionCount,
      status: bank.status,
    });
    setIsModalOpen(true);
  };

  const resetFormData = () => {
    setFormData({
      name: "",
      description: "",
      category: "",
      price: 0,
      questionCount: 0,
      status: "published",
    });
  };

  const handleAddNew = () => {
    setSelectedBank(null);
    resetFormData();
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "price" || name === "questionCount" ? Number(value) : value,
    }));
  };

  return (
    <div className="container py-10">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Question Banks</h1>
        <Button onClick={handleAddNew}>
          <Plus className="mr-2 h-4 w-4" /> Add Question Bank
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Manage Question Bank Packs</CardTitle>
          <CardDescription>
            Create and manage question banks that companies can purchase and use
            for assessments
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Questions</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {questionBanks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    {isLoading ? (
                      <div className="flex justify-center items-center space-x-2">
                        <div className="animate-spin h-5 w-5 border-t-2 border-b-2 border-primary rounded-full" />
                        <span>Loading question banks...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center space-y-2">
                        <Package className="h-12 w-12 text-muted-foreground" />
                        <p>No question banks found</p>
                        <Button variant="outline" onClick={handleAddNew}>
                          Add your first question bank
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                questionBanks.map((bank) => (
                  <TableRow key={bank._id}>
                    <TableCell className="font-medium">{bank.name}</TableCell>
                    <TableCell>{bank.category}</TableCell>
                    <TableCell>{bank.questionCount}</TableCell>
                    <TableCell>${bank.price.toFixed(2)}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          bank.status === "published"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {bank.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(bank)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDelete(bank._id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {selectedBank ? "Edit Question Bank" : "Create Question Bank"}
            </DialogTitle>
            <DialogDescription>
              {selectedBank
                ? "Update the details of this question bank"
                : "Add a new question bank that companies can purchase"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., JavaScript Programming Basics"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter a description of the question bank"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="e.g., Programming"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="questionCount">Number of Questions</Label>
                <Input
                  id="questionCount"
                  name="questionCount"
                  type="number"
                  value={formData.questionCount}
                  onChange={handleInputChange}
                  min={0}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price ($)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="pl-9"
                    min={0}
                    step={0.01}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsModalOpen(false);
                setSelectedBank(null);
                resetFormData();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateUpdate} disabled={isLoading}>
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin h-4 w-4 border-t-2 border-b-2 border-current rounded-full" />
                  <span>Saving...</span>
                </div>
              ) : selectedBank ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageQuestionBanks;
